import ListSubheader from "@material-ui/core/ListSubheader";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderGroupParams,
} from "@material-ui/lab/Autocomplete";
// @ts-expect-error TS7016: Could not find a declaration file for module 'autosuggest-highlight/match'. '/Users/goksel/Sites/carbon-website-next/node_modules/autosuggest-highlight/match/index.js' implicitly has an 'any' type.
import match from "autosuggest-highlight/match";
// @ts-expect-error TS7016: Could not find a declaration file for module 'autosuggest-highlight/parse'. '/Users/goksel/Sites/carbon-website-next/node_modules/autosuggest-highlight/parse/index.js' implicitly has an 'any' type.
import parse from "autosuggest-highlight/parse";
import * as CSS from "csstype";
import React, {ForwardRefRenderFunction, useState} from "react";
import {ListChildComponentProps, VariableSizeList} from "react-window";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const {data, index, style} = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: ((style as CSS.Properties).top + LISTBOX_PADDING) as number,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementContextWrapper: ForwardRefRenderFunction<HTMLDivElement> = (props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
};
OuterElementContextWrapper.displayName = "OuterElementContextWrapper";

const OuterElementType = React.forwardRef<HTMLDivElement>(OuterElementContextWrapper);

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement>(function ListboxComponent(props, ref) {
  const {children, ...other} = props;
  const itemData = React.Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {noSsr: true});
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const useStyles = makeStyles({
  listbox: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

const renderGroup = (params: AutocompleteRenderGroupParams) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
];

export type AutocompleteOnChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  value: string,
  reason?: AutocompleteChangeReason,
  details?: AutocompleteChangeDetails,
) => void;

type Props = {
  options: string[];
  onChange: AutocompleteOnChange;
  label?: string;
  className?: string;
  disabled?: boolean;
  clearOnEscape?: boolean;
};

const LongAutocomplete: React.FC<Props> = ({
  options,
  onChange,
  label,
  className,
  disabled,
  clearOnEscape,
}) => {
  const classes = useStyles();

  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    if (inputValue.length > 0) {
      setOpen(true);
    }
  };
  // @ts-expect-error TS7006, TS7006: Parameter 'event' implicitly has an 'any' type.,  Parameter 'newInputValue' implicitly has an 'any' type.
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  return (
    <Autocomplete
      disableListWrap
      classes={classes}
      className={className}
      ListboxComponent={ListboxComponent}
      renderGroup={renderGroup}
      options={options}
      renderInput={params => (
        <TextField {...params} variant="outlined" placeholder={label} type="search" />
      )}
      // @ts-expect-error TS2322: Type 'AutocompleteOnChange' is not assignable to type '(event: ChangeEvent<{}>, value: string, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<string> | undefined) => void'.
      onChange={onChange}
      disableClearable
      clearOnEscape={clearOnEscape}
      freeSolo
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      autoHighlight
      disabled={disabled}
      renderOption={(option, {inputValue}) => {
        const matches = match(option, inputValue);
        const parts = parse(option, matches);

        return (
          <div>
            {/* @ts-expect-error TS7006, TS7006: Parameter 'part' implicitly has an 'any' type.,  Parameter 'index' implicitly has an 'any' type. */}
            {parts.map((part, index) => (
              <span key={index} style={{fontWeight: part.highlight ? 700 : 400}}>
                {part.text}
              </span>
            ))}
          </div>
        );
      }}
    />
  );
};

export default LongAutocomplete;
